// FR-A3: request a password-reset email. POST /auth/forgot-password is always 204 regardless
// of whether the address exists (handlers::forgot_password's doc comment) — this page shows
// the same "check your email" state either way, on purpose.
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import { AuthShell } from "../components/AuthShell";
import { Input } from "@grackle/ui";
import { ButtonLink, FormNotice, LoadingButton } from "../components/form";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(undefined);
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError("Enter a valid email address");
      return;
    }
    setEmailError(undefined);
    setSubmitting(true);
    try {
      await api("POST", "/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setFormError(err instanceof ApiFail ? err.message : "Couldn't reach Grackle. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthShell
        title="Check your email"
        subtitle={`If an account exists for ${email.trim()}, we've sent a link to reset the password. It expires in 15 minutes.`}
      >
        <ButtonLink to="/signin" block>
          Back to sign in
        </ButtonLink>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter the email on your account and we'll send a link to reset it."
      footer={
        <>
          Remembered it? <Link to="/signin">Sign in</Link>.
        </>
      }
    >
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailError}
        />
        {formError ? <FormNotice tone="error">{formError}</FormNotice> : null}
        <LoadingButton type="submit" block loading={submitting}>
          {submitting ? "Sending…" : "Send reset link"}
        </LoadingButton>
      </form>
    </AuthShell>
  );
}
